import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { inboxAPI } from '../services/inboxAPI';

// Header bell with unread badge. Polls /student/inbox/unread-count on
// focus and every 120s. Tap → /inbox page (full-screen for now; we can
// swap to a slide-over panel later without changing this component).
//
// The on-focus refresh is the responsive path (badge updates the instant the
// user returns to the tab); the background interval is just a safety net, so
// 120s is plenty and halves this poller's request volume.
const POLL_MS = 120_000;

const InboxBell: React.FC<{ className?: string }> = ({ className }) => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const n = await inboxAPI.unreadCount();
      setUnread(n);
    } catch {
      // Silent — bell stays at last known count if the request fails.
    }
  }, []);

  useEffect(() => {
    // Only poll if logged in (token in localStorage). Otherwise the
    // request would 401 every minute, spamming logs.
    if (!localStorage.getItem('access_token')) return;

    refresh();
    const interval = window.setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const display = unread > 99 ? '99+' : String(unread);

  return (
    <button
      type="button"
      className={className}
      onClick={() => navigate('/inbox')}
      aria-label={unread ? `Inbox — ${unread} unread` : 'Inbox'}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        padding: 6,
        cursor: 'pointer',
        color: 'inherit',
      }}
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
      </svg>
      {unread > 0 && (
        <span
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            minWidth: 16,
            height: 16,
            padding: '0 4px',
            borderRadius: 8,
            background: '#dc2626',
            color: 'white',
            fontSize: 10,
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
          }}
        >
          {display}
        </span>
      )}
    </button>
  );
};

export default InboxBell;
